import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

interface Props {
  tone?: "light" | "dark";
}

/**
 * Fantasy.co-style subtle scroll indicator.
 * Fixed to viewport bottom-center, fades out as soon as the user scrolls.
 * Decorative — pointer-events none, aria-hidden.
 */
export default function ScrollCue({ tone = "light" }: Props) {
  const [opacity, setOpacity] = useState(1);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setReduceMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);

    const mq = window.matchMedia("(max-width: 767px)");
    const syncMobile = () => setIsMobile(mq.matches);
    syncMobile();
    mq.addEventListener("change", syncMobile);

    let raf = 0;
    let pending = false;
    const apply = () => {
      const y = window.scrollY;
      const base = Math.max(0, 1 - y / 80);
      // Cap baseline opacity on mobile so the cue stays whisper-quiet
      const cap = mq.matches ? 0.7 : 1;
      setOpacity(base * cap);
      pending = false;
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      raf = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      mq.removeEventListener("change", syncMobile);
      cancelAnimationFrame(raf);
    };
  }, []);

  // tone="light" = white text (on dark heroes); tone="dark" = near-black (on light heroes)
  const colorClass = tone === "light" ? "text-background/55" : "text-foreground/60";

  const bobKeyframe = isMobile ? "scroll-bob-sm" : "scroll-bob";

  return (
    <div
      aria-hidden
      className={`fixed left-1/2 -translate-x-1/2 z-30 pointer-events-none flex flex-col items-center gap-0 md:gap-2 bottom-[max(14px,calc(env(safe-area-inset-bottom)+8px))] md:bottom-5 lg:bottom-7 ${colorClass}`}
      style={{
        opacity,
        transition: "opacity 240ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <span className="t-micro uppercase tracking-[0.18em] hidden md:inline">Scroll</span>
      <ChevronDown
        size={isMobile ? 12 : 14}
        strokeWidth={isMobile ? 1.25 : 1.5}
        style={{
          animation: reduceMotion ? undefined : `${bobKeyframe} 2.4s ease-in-out infinite`,
        }}
      />
    </div>
  );
}
